import './meme.css';
import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext } from '../../context/AuthContext';
import axiosConn from '../../axiosConn';
import CreateComment from '../createComment/CreateComment';
import Comment from '../comment/Comment';
import Like from '../like/Like'; // adjust the path as needed

import { CircularProgress } from '@material-ui/core';
import { Link } from 'react-router-dom';
import ReactPlayer from 'react-player/lazy';
import { useMediaQuery } from 'react-responsive';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'timeago.js';
import preciseFormat from '../../utils/preciseFormat';
import ModalImage from 'react-modal-image';
import { useTheme } from '../../context/ThemeContext';
import ReactionBar from '../reactionBar/ReactionBar';
import { SocketContext } from '../../context/socket';
import { removeCommentById } from '../../utils/commentRealtime';

const hasPlayerLiked = (likes = [], playerId = '') =>
  Array.isArray(likes) &&
  likes.some((likePlayerId) => String(likePlayerId) === String(playerId));

export default function Meme({
  meme,
  parent = 'Meme', // optional: 'Meme' | 'MemeRotator'
  size, // may be undefined or 'compact' | 'comfortable' | 'auto' | 'xs' | 'xxs'
}) {
  const [memeState, setMemeState] = useState(meme);
  const PF = process.env.REACT_APP_PUBLIC_FOLDER;
  const { player } = useContext(AuthContext);
  const [like, setLike] = useState(meme && meme.likes ? meme.likes.length : 0);
  const [isLiked, setIsLiked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVideo, setIsVideo] = useState(false);
  const [commentCount, setCommentCount] = useState(1);
  const [comments, setComments] = useState(
    meme && meme.comments ? meme.comments : [],
  );
  const [views, setViews] = useState(meme.views);
  const imgRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(true);
  const { darkTheme } = useTheme();

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isVideoInView, setIsVideoInView] = useState(false);
  const [isTabActive, setIsTabActive] = useState(true);
  const [videoManuallyOff, setVideoManuallyOff] = useState(false);
  // const [memeToShow, SetMemeToShow] = useState(meme);
  const { socket: socket2 } = useContext(SocketContext);
  const activeMeme = memeState || meme;
  const currentPlayerId = player?.data?.player?.playerId || '';

  // Decide comment composer size (backward compatible)
  // Respect explicit size prop; fallback keeps old behavior
  const commentSize = size ?? (parent === 'MemeRotator' ? 'compact' : 'auto');

  // Sync local state when meme prop changes
  useEffect(() => {
    setMemeState(meme);
    setLike(meme?.likes?.length || 0);
  }, [meme]);

  useEffect(() => {
    setLike(activeMeme?.likes?.length || 0);
    setIsLiked(hasPlayerLiked(activeMeme?.likes, currentPlayerId));
  }, [activeMeme?.likes, currentPlayerId]);

  const handleMemeReact = async (type) => {
    await axiosConn.put(
      `memes/${meme._id}/react`,
      {
        playerId: player.data.player.playerId,
        type,
        profilePhoto: player.data.player.profilePhoto,
      },
      {
        headers: { Authorization: `Bearer ${player.token}` },
      },
    );
    // Fetch updated meme and update state
    const res = await axiosConn.get(`memes/${meme._id}`, {
      headers: { Authorization: `Bearer ${player.token}` },
    });
    setMemeState(res.data.data.data);
    socket2.emit('updateMeme', { updatedMeme: res.data.data.data });
  };

  const mobileView = useMediaQuery({
    query: '(max-width: 805px)',
  });

  useEffect(() => {
    //setting views
    try {
      axiosConn.put(
        'memes/' + meme._id + '/view',
        {},
        {
          headers: {
            Authorization: `Bearer ${player.token}`,
          },
        },
      );
    } catch (err) {
      console.log(err);
    }
    //console.log(meme);
    setViews((view) => view + 1);
    return () => {};
  }, [player.token, meme._id]);

  //console.log(meme);
  useEffect(() => {
    let mounted = true;
    if (meme.img) {
      const length = meme.img.split('.').length;
      //console.log(length);
      if (
        meme.img.split('.')[length - 1] === 'mp4' ||
        meme.img.split('.')[length - 1] === 'mov'
      ) {
        if (!mounted) return;
        setIsVideo(true);
        //console.log(post.img.split('.')[length - 1]);
      } else if (
        meme.img.includes('youtube.com') ||
        meme.img.includes('youtu.be') ||
        meme.img.includes('soundcloud.com') ||
        meme.img.includes('vimeo.com') ||
        meme.img.includes('twitch.com') ||
        meme.img.includes('streamable.com') ||
        meme.img.includes('wistia.com') ||
        meme.img.includes('dailymotion.com') ||
        meme.img.includes('mixcloud.com') ||
        meme.img.includes('vidyard.com') ||
        // meme.img.includes('brighteon.com') ||
        meme.img.includes('kaltura.com')
      ) {
        if (!mounted) return;
        setIsVideo(true);
      }
    }
    if (!mounted) return;
    return () => {
      mounted = false;
      setIsVideo(false);
    };
  }, [meme.img]);

  // Detect tab visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabActive(!document.hidden);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Detect if video is in viewport
  useEffect(() => {
    if (!isVideo) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setIsVideoInView(entry.isIntersecting && entry.intersectionRatio > 0.5);
      },
      { threshold: 0.5 },
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isVideo]);

  const likeHandler = async () => {
    // console.log(player);
    //console.log(meme.likes.length);
    try {
      const action = isLiked ? 'unlike' : 'like';
      const response = await axiosConn.put(
        'memes/' + meme._id + '/like',
        { action },
        {
          headers: {
            Authorization: `Bearer ${player.token}`,
          },
        },
      );

      const updatedMeme = response.data?.data?.meme;
      if (!updatedMeme) {
        console.warn('[Meme.likeHandler] Like response missing updated meme');
        return;
      }

      setMemeState(updatedMeme);
      setLike(updatedMeme.likes?.length || 0);
      setIsLiked(hasPlayerLiked(updatedMeme.likes, currentPlayerId));
      socket2.emit('updateMeme', { updatedMeme });
    } catch (err) {
      console.error(err);
    }
    //when like changes pull this meme again.
    // try {
    //   setIsLoading(true);
    //   const res = await axiosConn.get(
    //     '/memes/?_id=' + meme._id,

    //     {
    //       headers: {
    //         Authorization: `Bearer ${player.token}`,
    //       },
    //     },
    //   );
    //   console.log(res.data.data.data[0]);
    //   SetMemeToShow(res.data.data.data);
    //   setIsLoading(false);
    // } catch (err) {
    //   setIsLoading(false);
    // }
  };
  // console.log(meme);
  const showMore = () => {
    setCommentCount((prev) => prev + 3);
  };

  const handleCommentDeleted = useCallback((commentId, nextComments) => {
    setComments((prevComments = []) => {
      const updatedComments = Array.isArray(nextComments)
        ? nextComments
        : removeCommentById(prevComments, commentId);
      setCommentCount((currentCount) =>
        Math.min(
          Math.max(currentCount, 1),
          Math.max(updatedComments.length, 1),
        ),
      );
      return updatedComments;
    });
  }, []);

  useEffect(() => {
    if (!socket2 || !meme?._id) return undefined;

    const handleMemeCommentDeleted = ({
      memeId,
      commentId,
      comments: incoming,
    }) => {
      if (String(memeId || '') !== String(meme._id || '')) return;
      handleCommentDeleted(commentId, incoming);
    };

    socket2.on('meme:comment:deleted', handleMemeCommentDeleted);
    return () => {
      socket2.off('meme:comment:deleted', handleMemeCommentDeleted);
    };
  }, [handleCommentDeleted, meme?._id, socket2]);

  return (
    <>
      <div
        className={`${!mobileView ? 'meme' : ''} ${
          darkTheme ? 'memeDarkTheme' : 'meme'
        }`}
      >
        <div className={mobileView ? 'postWrapper' : null}>
          {/* <div className="memeContainerElement"> */}
          {isLoading ? (
            <div className="feedPostProgress">
              <CircularProgress color="inherit" size="10px" />
            </div>
          ) : (
            <div
              ref={imgRef}
              className="imgVidContainer"
              title={`${meme.title}`}
            >
              {isVideo ? (
                <div
                  ref={containerRef}
                  style={{ position: 'relative', cursor: 'pointer' }}
                  onClick={() => setVideoManuallyOff((v) => !v)}
                  title={
                    videoManuallyOff
                      ? 'Click to turn ON video'
                      : 'Click to turn OFF video'
                  }
                >
                  {isLoaded && <div>Loading...</div>}
                  <ReactPlayer
                    ref={videoRef}
                    playing={isVideoInView && isTabActive && !videoManuallyOff}
                    muted={!(isVideoInView && isTabActive && !videoManuallyOff)}
                    width="100%"
                    height="100%"
                    loop={true}
                    controls={false}
                    url={meme.img}
                    onReady={() => setIsLoaded(false)}
                    config={{
                      file: {
                        attributes: {
                          controlsList: 'nodownload noremoteplayback',
                          disablePictureInPicture: true,
                        },
                      },
                    }}
                  />
                  {/* Optionally, show a visual indicator */}
                  {/* {videoManuallyOff && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'rgba(0,0,0,0.4)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 24,
                        pointerEvents: 'none',
                      }}
                    >
                      Video Off (click to turn on)
                    </div>
                  )} */}
                </div>
              ) : meme.img ? (
                // ...existing ModalImage code...
                <ModalImage
                  hideDownload={true}
                  small={meme.img}
                  large={meme.img}
                  className="memeImg2 loadingImg meme-img"
                  alt=""
                  title={`${meme.title}`}
                />
              ) : null}
              <div className="memeByNameContainer">
                <Link className="profileLink" to={`/profile/${meme.playerId}`}>
                  <span className="memeByName">Meme by {meme.playerId}</span>
                </Link>
                <div className={!mobileView ? 'memeViews' : 'memeViewsMobile'}>
                  <VisibilityIcon sx={{ color: 'grey', fontSize: 15 }} />

                  {isNaN(views) ? 0 : views}
                </div>
                <span className={!mobileView ? 'postDate' : 'postDateMobile'}>
                  {preciseFormat(meme.createdAt)}
                </span>
              </div>
              <div className="memeBottom">
                <Like
                  imgRef={imgRef}
                  text="+2 JGK, Yay!"
                  likes={like}
                  likesProfileImgs={activeMeme?.likesProfileImgs}
                  isLiked={isLiked}
                  likeHandler={likeHandler}
                  mobileView={mobileView}
                  PF={PF}
                  title={meme.title}
                  width={window.innerWidth}
                  textOffset={-15}
                />

                {/* <div className="memeBottomRight"></div> */}
              </div>
              {/* Add ReactionBar for memes */}
              <div style={{ margin: '6px 0' }}>
                <ReactionBar
                  post={activeMeme}
                  currentPlayer={player.data.player}
                  onReact={handleMemeReact}
                />
              </div>
              <div className="commentBottom">
                <div className="comments_wrap">
                  <CreateComment
                    // postId={meme._id}
                    memeId={meme._id}
                    setComments={setComments}
                    setCommentCount={setCommentCount}
                    parent={parent === 'MemeRotator' ? 'MemeRotator' : 'Meme'}
                    size={
                      parent === 'MemeRotator'
                        ? commentSize || 'xxs'
                        : commentSize
                    }
                  />
                  <span className="postCommentText">
                    {comments &&
                      comments
                        .sort((a, b) => {
                          return new Date(b.commentAt) - new Date(a.commentAt);
                        })
                        .slice(0, commentCount)

                        .map((comment, i) => (
                          <Comment
                            comment={comment}
                            key={i}
                            // postId={meme._id}
                            memeId={meme._id}
                            parent="Meme"
                            parentOwnerPlayerId={meme.playerId}
                            onDelete={handleCommentDeleted}
                          />
                        ))}
                    {comments && commentCount < comments.length && (
                      <div
                        className={
                          !mobileView ? 'viewComments' : 'viewCommentsMobile'
                        }
                        onClick={() => showMore()}
                      >
                        View {comments.length - commentCount} more{' '}
                        {comments.length - commentCount === 1
                          ? 'comment'
                          : 'comments'}
                      </div>
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}
          {/* </div> */}
        </div>
      </div>
    </>
  );
}
